<template>
  <div class="hero-rabbit" :class="{ 'is-still': heroState.reducedMotion }" aria-hidden="true">
    <!-- Parallax wrapper: same depth as the meadow, so the rabbit never slides
         on the ground. Everything inside is drawn in CSS pixels (no viewBox)
         from the rabbit size --rb-s; HeroRabbitRig.js has the rig. -->
    <div ref="stage" class="hero-rabbit__stage">
      <svg ref="svg" class="hero-rabbit__svg" xmlns="http://www.w3.org/2000/svg" focusable="false">
        <defs>
          <radialGradient :id="uid + '-shadow'">
            <stop offset="0" class="rb-g-shadow-0"/>
            <stop offset="0.6" class="rb-g-shadow-1"/>
            <stop offset="1" class="rb-g-shadow-2"/>
          </radialGradient>
          <radialGradient :id="uid + '-puff'">
            <stop offset="0" class="rb-g-puff-0"/>
            <stop offset="0.55" class="rb-g-puff-1"/>
            <stop offset="1" class="rb-g-puff-2"/>
          </radialGradient>
          <radialGradient :id="uid + '-hi'">
            <stop offset="0" class="rb-g-hi-0"/>
            <stop offset="1" class="rb-g-hi-1"/>
          </radialGradient>
          <radialGradient :id="uid + '-soft'">
            <stop offset="0" class="rb-g-soft-0"/>
            <stop offset="1" class="rb-g-soft-1"/>
          </radialGradient>
          <radialGradient :id="uid + '-cheek'">
            <stop offset="0" class="rb-g-cheek-0"/>
            <stop offset="1" class="rb-g-cheek-1"/>
          </radialGradient>
          <linearGradient :id="uid + '-under'" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" class="rb-g-under-0"/>
            <stop offset="1" class="rb-g-under-1"/>
          </linearGradient>
          <linearGradient :id="uid + '-rim'" gradientUnits="userSpaceOnUse" x1="0" y1="-25" x2="0" y2="-9">
            <stop offset="0" class="rb-g-rim-0"/>
            <stop offset="1" class="rb-g-rim-1"/>
          </linearGradient>
          <linearGradient :id="uid + '-headrim'" gradientUnits="userSpaceOnUse" x1="6" y1="-24" x2="9" y2="-13">
            <stop offset="0" class="rb-g-rim-0"/>
            <stop offset="1" class="rb-g-rim-1"/>
          </linearGradient>
          <radialGradient :id="uid + '-iris'" cx="0.42" cy="0.68" r="0.62">
            <stop offset="0" class="rb-g-iris-0"/>
            <stop offset="1" class="rb-g-iris-1"/>
          </radialGradient>
          <!-- Fluffy tail: bright core, softer and slightly see-through edge. -->
          <radialGradient :id="uid + '-tail'" cx="0.42" cy="0.36" r="0.66">
            <stop offset="0.25" class="rb-g-tail-0"/>
            <stop offset="0.8" class="rb-g-tail-1"/>
            <stop offset="1" class="rb-g-tail-2"/>
          </radialGradient>
          <radialGradient :id="uid + '-hole'" cx="0.5" cy="0.7" r="0.66">
            <stop offset="0.25" class="rb-g-hole-0"/>
            <stop offset="1" class="rb-g-hole-1"/>
          </radialGradient>
          <linearGradient :id="uid + '-mound'" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" class="rb-g-mound-0"/>
            <stop offset="0.6" class="rb-g-mound-1"/>
            <stop offset="1" class="rb-g-mound-2"/>
          </linearGradient>
          <clipPath :id="uid + '-torso'"><path ref="torsoClip"/></clipPath>
          <clipPath :id="uid + '-head'"><path :d="$options.shapes.head"/></clipPath>
          <clipPath :id="uid + '-thigh'"><path :d="$options.shapes.thigh"/></clipPath>
          <clipPath :id="uid + '-eye'"><circle :cx="$options.geo.eye[0]" :cy="$options.geo.eye[1]" r="4.6"/></clipPath>
          <!-- Hides whatever is below the ground while the rabbit is in its
               burrow: everything above the hole line, the mouth of the hole,
               and the meadow away from the hole down to the feet line (only
               down to the hole line while it dives). -->
          <clipPath :id="uid + '-burrow'" clipPathUnits="userSpaceOnUse">
            <rect ref="clipTop" x="-20000" y="-20000" width="40000" height="20000"/>
            <rect ref="clipLeft" x="-20000" y="-20000" width="20000" height="20000"/>
            <rect ref="clipRight" x="0" y="-20000" width="20000" height="20000"/>
            <ellipse ref="clipHole" cx="0" cy="0" rx="1" ry="1"/>
          </clipPath>
        </defs>

        <!-- The burrow: a grassy mound with a dark opening and a fan of earth. -->
        <g class="rb-burrow-in">
          <g ref="burrow">
            <path :d="$options.shapes.mound" :fill="url('mound')"/>
            <path :d="$options.shapes.bladesBack" class="f-blade"/>
            <path :d="$options.shapes.bladesBackHi" class="f-bladehi"/>
            <path :d="$options.shapes.earth" class="f-earth"/>
            <path :d="$options.shapes.hole" :fill="url('hole')"/>
            <path :d="$options.shapes.holeFloor" class="f-earth" opacity="0.6"/>
            <path :d="$options.shapes.overhang" :fill="url('mound')"/>
            <g>
              <path :d="$options.shapes.lip" class="f-earthhi"/>
              <ellipse cx="-9" cy="3.9" rx="1.5" ry="0.95" class="f-earth"/>
              <ellipse cx="11.5" cy="4.1" rx="1.1" ry="0.75" class="f-earth"/>
              <path :d="$options.shapes.bladesFront" class="f-blade"/>
              <path :d="$options.shapes.bladesFrontHi" class="f-bladehi"/>
            </g>
          </g>
        </g>

        <g ref="shadow" opacity="0">
          <ellipse cx="0" cy="0" rx="30" ry="4.6" :fill="url('shadow')"/>
        </g>

        <!-- Soft puff where the rabbit lands after jumping out of the hole. -->
        <g ref="puff" opacity="0" display="none">
          <ellipse cx="0" cy="-3" rx="34" ry="8" :fill="url('puff')"/>
        </g>

        <g ref="clipWrap">
          <g ref="rig" visibility="hidden">
            <!-- far side (darker) -->
            <g ref="hFshin"><path :d="$options.shapes.shin" class="f-deep"/></g>
            <g ref="hFfoot"><path :d="$options.shapes.foot" class="f-deep"/></g>
            <g ref="hFthigh"><path :d="$options.shapes.thigh" class="f-deep"/></g>
            <g ref="fFupper"><path :d="$options.shapes.upperArm" class="f-deep"/></g>
            <g ref="fFfore"><path :d="$options.shapes.forearm" class="f-deep"/></g>
            <g ref="fFpaw"><path :d="$options.shapes.paw" class="f-shade"/></g>
            <g ref="fNupper"><path :d="$options.shapes.upperArm" class="f-fur"/></g>

            <g ref="torso">
              <path ref="torsoPath" class="f-fur"/>
              <g :clip-path="url('torso')">
                <ellipse cx="-10" cy="-15" rx="26" ry="8.5" :fill="url('hi')"/>
                <ellipse cx="17" cy="7" rx="13" ry="10" transform="rotate(-25 17 7)" class="f-belly" opacity="0.28"/>
                <ellipse cx="-4" cy="17.5" rx="25" ry="5.8" class="f-belly" opacity="0.45"/>
                <rect x="-50" y="3" width="100" height="22" :fill="url('under')"/>
                <path ref="torsoRim" class="rb-rim" fill="none" :stroke="url('rim')" stroke-width="3"/>
              </g>
            </g>

            <g ref="tail">
              <path :d="$options.shapes.tail" :fill="url('tail')"/>
            </g>

            <!-- near side -->
            <g ref="hNshin"><path :d="$options.shapes.shin" class="f-shade"/></g>
            <g ref="hNfoot">
              <path :d="$options.shapes.foot" class="f-fur"/>
              <ellipse cx="13" cy="-1.4" rx="11" ry="1.5" class="f-furhi" opacity="0.18"/>
            </g>
            <g ref="hNthigh">
              <path :d="$options.shapes.thigh" class="f-fur"/>
              <g :clip-path="url('thigh')">
                <ellipse cx="2" cy="-9" rx="15" ry="7" :fill="url('hi')"/>
                <ellipse cx="11" cy="11" rx="17" ry="8" :fill="url('soft')"/>
              </g>
            </g>
            <g ref="fNfore" :id="uid + '-fore'"><path :d="$options.shapes.forearm" class="f-fur"/></g>
            <g ref="fNpaw" :id="uid + '-paw'">
              <path :d="$options.shapes.paw" class="f-fur"/>
              <ellipse cx="5" cy="-0.4" rx="3.6" ry="1.8" class="f-belly" opacity="0.2"/>
            </g>

            <g ref="head">
              <g ref="headInner">
                <g ref="earF">
                  <g transform="scale(0.94)">
                    <path :d="$options.shapes.ear" class="f-deep"/>
                    <path :d="$options.shapes.earInner" class="f-earin" opacity="0.22"/>
                    <path :d="$options.shapes.earTip" class="f-tip" opacity="0.8"/>
                  </g>
                </g>
                <path :d="$options.shapes.head" class="f-fur"/>
                <g :clip-path="url('head')">
                  <ellipse cx="10" cy="-18" rx="13" ry="6" :fill="url('hi')"/>
                  <ellipse cx="10" cy="6" rx="16" ry="5" class="f-shade" opacity="0.38"/>
                  <ellipse cx="27" cy="-4.6" rx="5.8" ry="4.5" class="f-belly" opacity="0.14"/>
                  <g ref="farEye" opacity="0">
                    <ellipse cx="23.6" cy="-14.4" rx="1" ry="1.5" class="f-eye"/>
                  </g>
                  <path :d="$options.shapes.head" class="rb-rim" fill="none" :stroke="url('headrim')" stroke-width="2.4"/>
                </g>
                <g ref="eyeG">
                  <circle ref="eyeRing" :cx="$options.geo.eye[0]" :cy="$options.geo.eye[1]" r="3.4" class="f-shade" opacity="0"/>
                  <ellipse :cx="$options.geo.eye[0]" :cy="$options.geo.eye[1]" rx="2.5" ry="2.2" class="f-eye"/>
                  <g :clip-path="url('eye')">
                    <g ref="lid">
                      <rect x="9" y="-28" width="11.5" height="11" class="f-fur"/>
                    </g>
                  </g>
                  <!-- the closed eye: a soft lash line once the lid is down -->
                  <path ref="eyeShut" :d="$options.shapes.eyeShut" class="s-line" fill="none" stroke-width="0.6" stroke-linecap="round" opacity="0"/>
                </g>
                <g ref="muzzle">
                  <g ref="nose">
                    <path :d="$options.shapes.nose" class="f-nose"/>
                  </g>
                </g>
                <g ref="earN">
                  <path :d="$options.shapes.ear" class="f-fur"/>
                  <path :d="$options.shapes.earEdge" class="f-shade" opacity="0.55"/>
                  <path :d="$options.shapes.earInner" class="f-earin" opacity="0.4"/>
                  <path :d="$options.shapes.earTip" class="f-tip" opacity="0.75"/>
                  <path :d="$options.shapes.earRim" class="rb-rim s-rim" fill="none" stroke-width="0.8" stroke-linecap="round" opacity="0.35"/>
                </g>
              </g>
            </g>

            <!-- While it washes its face the near paw passes in front of it. -->
            <g ref="pawsFront" visibility="hidden">
              <use :href="'#' + uid + '-fore'" :xlink:href="'#' + uid + '-fore'"/>
              <use :href="'#' + uid + '-paw'" :xlink:href="'#' + uid + '-paw'"/>
            </g>
          </g>
        </g>

        <!-- The front lip of the hole again, drawn over the rabbit while it is
             going in or out so its body disappears behind the rim (settles in
             with the burrow). -->
        <g class="rb-burrow-in">
          <g ref="lipFront" opacity="0">
            <path :d="$options.shapes.lip" class="f-earthhi"/>
            <ellipse cx="-9" cy="3.9" rx="1.5" ry="0.95" class="f-earth"/>
            <ellipse cx="11.5" cy="4.1" rx="1.1" ry="0.75" class="f-earth"/>
            <path :d="$options.shapes.bladesFront" class="f-blade"/>
            <path :d="$options.shapes.bladesFrontHi" class="f-bladehi"/>
          </g>
        </g>

        <!-- Earth kicked out of the hole (burrow units, mirrored by direction). -->
        <g ref="crumbs" visibility="hidden" display="none">
          <circle v-for="i in $options.crumbCount" :key="i" ref="crumb" cx="0" cy="0" :r="1.7 + (i % 3) * 0.55" class="f-crumb"/>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import { createLoop, seededRandom, exposeDebug, removeDebug } from './heroMotion';
import { SHAPES, HEAD_GEO, HEAD_SCALE, HOLE, POSES, newSolved, solvePose, torsoPath, copyPose } from './HeroRabbitRig';
import { ENTRANCE_HOLD, burrowX, createPlanner, evaluate, newFrame, noseAt, interruptWithHop, replan } from './HeroRabbitPlan';

const CRUMBS = 6;
const CRUMB_MS = 640;

function n2(v) {
  return Math.round(v * 100) / 100;
}

function n4(v) {
  return Math.round(v * 10000) / 10000;
}

function smooth(a, b, v) {
  const t = Math.min(Math.max((v - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
}

function placeBone(el, r, i) {
  el.setAttribute('transform', 'translate(' + n2(r[i]) + ' ' + n2(r[i + 1]) + ') rotate(' + n2(r[i + 2]) + ')');
}

export default {
  name: 'HeroRabbit',
  inject: ['heroState'],
  shapes: SHAPES,
  geo: HEAD_GEO,
  crumbCount: CRUMBS,
  data() {
    return {
      uid: 'rb' + Math.random().toString(36).slice(2, 8)
    };
  },
  watch: {
    'heroState.running'() {
      this.syncRunning();
    },
    'heroState.reducedMotion'() {
      this.syncRunning();
    },
    'heroState.width'() {
      this.$nextTick(this.onResize);
    }
  },
  created() {
    // Animation state lives outside Vue's reactivity on purpose: it changes
    // every frame and only ever reaches the DOM through setAttribute.
    this.seed = (Math.random() * 2147483647) | 0;
    this.simT = 0;
    this.seen = false;
    this.env = { W: 1, S: 60 };
    this.k = 0.6;
    this.groundY = 0;
    this.holeY = 0;
    this.feetY = 0;
    this.frame = newFrame();
    this.solved = newSolved();
    this.debugPaused = false;
    this.debugPose = null;
    this.debugU = 0.5;
    this.debugFace = 1;
    this.cache = {};
    // Each crumb's own throw (burrow units, per second), fixed per visit.
    const rnd = seededRandom(this.seed ^ 0x5bd1e995);
    this.crumbs = [];
    for (let i = 0; i < CRUMBS; i++) {
      this.crumbs.push({
        x0: (rnd() - 0.5) * 26,
        vx: 25 + rnd() * 75,
        vy: -(70 + rnd() * 90),
        rest: 1 + rnd() * 4,
        delay: rnd() * 90
      });
    }
  },
  mounted() {
    const r = this.$refs;
    this.els = {
      rig: r.rig, clipWrap: r.clipWrap, shadow: r.shadow, puff: r.puff, burrow: r.burrow, lipFront: r.lipFront,
      torso: r.torso, torsoPath: r.torsoPath, torsoClip: r.torsoClip, torsoRim: r.torsoRim,
      hNthigh: r.hNthigh, hNshin: r.hNshin, hNfoot: r.hNfoot,
      hFthigh: r.hFthigh, hFshin: r.hFshin, hFfoot: r.hFfoot,
      fNupper: r.fNupper, fNfore: r.fNfore, fNpaw: r.fNpaw,
      fFupper: r.fFupper, fFfore: r.fFfore, fFpaw: r.fFpaw,
      head: r.head, headInner: r.headInner, earN: r.earN, earF: r.earF,
      eyeG: r.eyeG, eyeRing: r.eyeRing, eyeShut: r.eyeShut, farEye: r.farEye, lid: r.lid,
      muzzle: r.muzzle, nose: r.nose, tail: r.tail, pawsFront: r.pawsFront,
      crumbs: r.crumbs, crumb: r.crumb || [],
      clipTop: r.clipTop, clipLeft: r.clipLeft, clipRight: r.clipRight, clipHole: r.clipHole
    };
    this.planner = createPlanner(this.seed);
    this.loop = createLoop((dt) => {
      this.simT += dt;
      // The peek-out waits until the burrow is actually on screen (a short
      // landscape phone shows the hero text first).
      if (!this.seen && this.simT > ENTRANCE_HOLD) this.simT = ENTRANCE_HOLD;
      this.draw();
    });
    this.watchVisibility();
    this.measure();
    this.draw();
    this.syncRunning();

    exposeDebug('rabbit', {
      pause: () => {
        this.debugPaused = true;
        this.loop.stop();
      },
      resume: () => {
        this.debugPaused = false;
        this.debugPose = null;
        this.syncRunning();
      },
      renderAt: (ms) => {
        this.debugPose = null;
        this.seen = true;
        this.simT = Math.max(0, Number(ms) || 0);
        this.draw();
        return this.frame.seg;
      },
      // setPose(name, u = 0.5, face = 1): a named pose at u * width.
      setPose: (name, u, face) => {
        if (!POSES[name]) return Object.keys(POSES);
        this.debugPaused = true;
        this.loop.stop();
        this.debugPose = name;
        this.debugU = typeof u === 'number' ? u : 0.5;
        this.debugFace = face === -1 ? -1 : 1;
        this.draw();
        return name;
      },
      triggerHop: () => {
        this.debugPose = null;
        const ok = interruptWithHop(this.planner, this.simT, this.env);
        this.draw();
        return ok;
      },
      // Re-measure after changing --rb-s by hand (e.g. to magnify the rig).
      relayout: () => {
        this.relayout();
        this.draw();
      },
      info: () => ({
        t: Math.round(this.simT),
        seg: this.frame.seg,
        x: Math.round(this.frame.x),
        size: Math.round(this.env.S),
        seen: this.seen,
        timeline: this.planner.segs.map((s) => s.type + '@' + Math.round(s.t0) +
          (s.events ? ':' + s.events.map((e) => e.kind).join('+') : ''))
      })
    });
  },
  beforeDestroy() {
    if (this.loop) this.loop.stop();
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    removeDebug('rabbit');
  },
  methods: {
    url(name) {
      return 'url(#' + this.uid + '-' + name + ')';
    },
    watchVisibility() {
      const stage = this.$refs.stage;
      if (!stage || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
        this.seen = true;
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        const e = entries[entries.length - 1];
        if (e.isIntersecting && e.intersectionRatio >= 0.55) {
          this.seen = true;
          this.observer.disconnect();
          this.observer = null;
        }
      }, { threshold: [0, 0.55, 1] });
      this.observer.observe(stage);
    },
    syncRunning() {
      if (!this.loop) return;
      if (this.heroState.reducedMotion) {
        this.loop.stop();
        this.draw();
        return;
      }
      if (this.heroState.running && !this.debugPaused) this.loop.start();
      else this.loop.stop();
    },
    onResize() {
      if (this._isDestroyed || !this.loop) return;
      this.relayout();
      // A paused or still scene must also follow the new layout.
      if (!this.loop.isRunning()) this.draw();
    },
    // Re-measures; when the size really changed, what was planned after the
    // current action is planned again for it (hop timings were made for the
    // old hop lengths, e.g. a phone rotated mid-run).
    relayout() {
      const W = this.env.W;
      const S = this.env.S;
      this.measure();
      if (Math.abs(this.env.W - W) > 0.5 || Math.abs(this.env.S - S) > 0.05) {
        replan(this.planner, this.simT, this.env);
      }
    },
    // Reads the stage size: its height is 1.7 rabbit sizes with the ground
    // 0.3 sizes above its bottom edge (see the stage CSS).
    measure() {
      const svg = this.$refs.svg;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const W = rect.width;
      const S = rect.height / 1.7;
      const k = S / 100;
      this.env.W = W;
      this.env.S = S;
      this.k = k;
      this.groundY = rect.height - 0.3 * S;

      const E = this.els;
      const Bx = burrowX(this.env);
      this.holeY = this.groundY - 1.5 * k;
      this.feetY = this.groundY + 1.5 * k;
      const tf = 'translate(' + n2(Bx) + ' ' + n2(this.holeY) + ') scale(' + n4(k) + ')';
      E.burrow.setAttribute('transform', tf);
      E.lipFront.setAttribute('transform', tf);
      E.clipTop.setAttribute('height', n2(20000 + this.holeY));
      E.clipLeft.setAttribute('width', n2(20000 + Bx - (HOLE.rx + 4) * k));
      E.clipRight.setAttribute('x', n2(Bx + (HOLE.rx + 4) * k));
      E.clipHole.setAttribute('cx', n2(Bx));
      E.clipHole.setAttribute('cy', n2(this.holeY));
      E.clipHole.setAttribute('rx', n2(HOLE.rx * k));
      E.clipHole.setAttribute('ry', n2(HOLE.ry * k));
      this.cache.deep = null;
    },
    draw() {
      if (!this.els) return;
      if (this.heroState.reducedMotion) {
        this.drawStill();
        return;
      }
      if (this.debugPose) {
        this.drawPose(POSES[this.debugPose]);
        return;
      }
      const fr = evaluate(this.planner, this.simT, this.env, this.frame);
      this.render(fr, this.simT);
    },
    // Reduced motion: sitting next to its burrow, in profile.
    drawStill() {
      const fr = this.frame;
      copyPose(fr.pose, POSES.sit);
      fr.pose.ht = 0;
      fr.pose.hr = 3;
      fr.pose.eN = -6;
      fr.pose.eF = -26;
      this.placeStatic(fr, burrowX(this.env) + 1.3 * this.env.S, 1);
      this.render(fr, 0);
    },
    drawPose(pose) {
      const fr = this.frame;
      copyPose(fr.pose, pose);
      this.placeStatic(fr, this.env.W * this.debugU, this.debugFace);
      this.render(fr, this.simT);
    },
    placeStatic(fr, x, face) {
      fr.visible = true;
      fr.x = x;
      fr.face = face;
      fr.rootY = 0;
      fr.clip = false;
      fr.deep = false;
      fr.ground = true;
      fr.lip = 0;
      fr.shadow = 1;
      fr.nose = 0;
      fr.chew = 0;
      fr.blink = 0;
      fr.paws = 0;
      fr.crumbT = -1;
      fr.puffT = -1;
      fr.seg = 'still';
    },
    // Attribute writes that rarely change are skipped when unchanged.
    setOnce(el, key, name, value) {
      if (this.cache[key] === value) return;
      this.cache[key] = value;
      el.setAttribute(name, value);
    },
    render(fr, t) {
      const E = this.els;
      const k = this.k;
      this.setOnce(E.clipWrap, 'clip', 'clip-path', fr.clip ? this.url('burrow') : 'none');
      if (this.cache.deep !== fr.deep) {
        // Diving: nothing may show below the rim, even beside the hole.
        this.cache.deep = fr.deep;
        const h = n2(20000 + (fr.deep ? this.holeY : this.feetY));
        E.clipLeft.setAttribute('height', h);
        E.clipRight.setAttribute('height', h);
      }
      this.setOnce(E.lipFront, 'lip', 'opacity', fr.lip ? '1' : '0');
      this.renderCrumbs(fr);
      this.renderPuff(fr);
      if (!fr.visible) {
        this.setOnce(E.rig, 'vis', 'visibility', 'hidden');
        this.setOnce(E.shadow, 'shadowOp', 'opacity', '0');
        return;
      }
      this.setOnce(E.rig, 'vis', 'visibility', 'visible');
      this.setOnce(E.pawsFront, 'paws', 'visibility', fr.paws ? 'visible' : 'hidden');

      const P = fr.pose;
      const R = solvePose(P, this.solved, fr.ground ? fr.rootY : null);
      E.rig.setAttribute('transform', 'translate(' + n2(fr.x) + ' ' + n2(this.groundY) + ') scale(' +
        n4(fr.face * k) + ' ' + n4(k) + ') translate(0 ' + n2(fr.rootY) + ')');

      E.torso.setAttribute('transform', 'translate(' + n2(R.torso[0]) + ' ' + n2(R.torso[1]) + ') rotate(' + n2(R.torso[2]) + ')');
      const d = torsoPath(P.st);
      E.torsoPath.setAttribute('d', d);
      E.torsoClip.setAttribute('d', d);
      E.torsoRim.setAttribute('d', d);

      placeBone(E.hNthigh, R.hN, 0);
      placeBone(E.hNshin, R.hN, 3);
      placeBone(E.hNfoot, R.hN, 6);
      placeBone(E.hFthigh, R.hF, 0);
      placeBone(E.hFshin, R.hF, 3);
      placeBone(E.hFfoot, R.hF, 6);
      placeBone(E.fNupper, R.fN, 0);
      placeBone(E.fNfore, R.fN, 3);
      placeBone(E.fNpaw, R.fN, 6);
      placeBone(E.fFupper, R.fF, 0);
      placeBone(E.fFfore, R.fF, 3);
      placeBone(E.fFpaw, R.fF, 6);
      placeBone(E.head, R.head, 0);
      E.tail.setAttribute('transform', 'translate(' + n2(R.tail[0]) + ' ' + n2(R.tail[1]) + ') rotate(' +
        n2(R.tail[2]) + ') scale(' + n4(0.88 * P.ts) + ')');

      // Turning the head toward the viewer: the face narrows, the near eye
      // slides forward, the far eye peeks over the nose bridge and the ears
      // part.
      const ht = P.ht;
      const pv = HEAD_GEO.turnPivot;
      E.headInner.setAttribute('transform', 'translate(' + pv[0] + ' ' + pv[1] + ') scale(' +
        n4(HEAD_SCALE * (1 - 0.1 * ht)) + ' ' + HEAD_SCALE + ') translate(' + (-pv[0]) + ' ' + (-pv[1]) + ')');
      E.eyeG.setAttribute('transform', 'translate(' + n2(2.2 * ht) + ' ' + n2(0.3 * ht) + ')');
      E.farEye.setAttribute('opacity', n2(Math.min(1, ht * 1.4)));
      const en = HEAD_GEO.earNear;
      const ef = HEAD_GEO.earFar;
      E.earN.setAttribute('transform', 'translate(' + en[0] + ' ' + en[1] + ') rotate(' + n2(P.eN + 9 * ht) +
        ') scale(1 ' + n4(P.eNs) + ')');
      E.earF.setAttribute('transform', 'translate(' + ef[0] + ' ' + ef[1] + ') rotate(' + n2(P.eF - 7 * ht) +
        ') scale(1 ' + n4(P.eFs) + ')');

      // Blink: the fur lid slides over the eye, the pale eye ring fades and
      // a lash line marks the closed eye.
      const bl = fr.blink;
      E.lid.setAttribute('transform', 'translate(0 ' + n2(bl * 9.4) + ')');
      this.setOnce(E.eyeRing, 'ring', 'opacity', '0');
      this.setOnce(E.eyeShut, 'shut', 'opacity', String(n2(smooth(0.55, 1, bl))));
      const nz = noseAt(t) * fr.nose;
      const chew = fr.chew * Math.sin(t / 1000 * Math.PI * 2 * 4.6);
      E.nose.setAttribute('transform', 'translate(' + n2(-0.15 * nz) + ' ' + n2(0.45 * nz) + ')');
      E.muzzle.setAttribute('transform', 'translate(' + n2(0.3 * chew) + ' ' + n2(0.18 * nz + 0.45 * chew) + ')');

      // Soft contact shadow under the body: smaller and fainter in the air.
      const sign = fr.face < 0 ? -1 : 1;
      const lift = Math.min(Math.max(-fr.rootY, 0) / 42, 1);
      const sx = (0.8 + 0.25 * Math.abs(Math.cos(P.cr * Math.PI / 180)) + 0.12 * P.st) * (1 - 0.42 * lift);
      E.shadow.setAttribute('transform', 'translate(' + n2(fr.x + sign * P.cx * k * Math.abs(fr.face)) + ' ' +
        n2(this.groundY) + ') scale(' + n4(sx * k) + ' ' + n4(k * (1 - 0.3 * lift)) + ')');
      this.setOnce(E.shadow, 'shadowOp', 'opacity', String(n2(fr.shadow * (1 - 0.55 * lift))));
    },
    // A few crumbs of earth thrown from the mouth of the hole: each flies on
    // its own parabola, lands just outside the hole and fades.
    renderCrumbs(fr) {
      const E = this.els;
      const ct = fr.crumbT;
      const on = ct >= 0 && ct < CRUMB_MS;
      this.setOnce(E.crumbs, 'crumbs', 'visibility', on ? 'visible' : 'hidden');
      if (!on) return;
      const k = this.k;
      E.crumbs.setAttribute('transform', 'translate(' + n2(burrowX(this.env)) + ' ' + n2(this.holeY) + ') scale(' +
        n4(fr.crumbDir * k) + ' ' + n4(k) + ')');
      const g = 640;
      for (let i = 0; i < E.crumb.length; i++) {
        const c = this.crumbs[i];
        const s = Math.max(ct - c.delay, 0) / 1000;
        // Lands on the turf in front of the hole.
        const tLand = (-c.vy + Math.sqrt(c.vy * c.vy + 2 * g * (c.rest + 2))) / g;
        const tf = Math.min(s, tLand);
        const x = c.x0 + c.vx * tf;
        const y = -2 + c.vy * tf + 0.5 * g * tf * tf;
        const op = s <= 0 ? 0 : (1 - smooth(CRUMB_MS * 0.55, CRUMB_MS, ct)) * Math.min(1, s * 30);
        E.crumb[i].setAttribute('transform', 'translate(' + n2(x) + ' ' + n2(y) + ')');
        E.crumb[i].setAttribute('opacity', n2(op));
      }
    },
    renderPuff(fr) {
      const E = this.els;
      const pt = fr.puffT;
      const on = pt >= 0 && pt < 620;
      if (!on) {
        this.setOnce(E.puff, 'puffOp', 'opacity', '0');
        return;
      }
      const u = pt / 620;
      const grow = 1 - (1 - u) * (1 - u);
      const k = this.k;
      E.puff.setAttribute('transform', 'translate(' + n2(fr.puffX) + ' ' + n2(this.groundY) + ') scale(' +
        n4(k * (0.55 + 0.95 * grow)) + ' ' + n4(k * (0.6 + 0.6 * grow)) + ')');
      this.setOnce(E.puff, 'puffOp', 'opacity', String(n2(Math.min(1, pt / 70) * (1 - u) * (1 - u))));
    }
  }
}
</script>

<style scoped lang="scss">
.hero-rabbit {
  // Body length of the rabbit; every size in the SVG derives from it. About
  // half to two-thirds the height of the trees just behind it, so the forest
  // keeps its scale.
  --rb-s: clamp(22px, 2.4vw, 36px);
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(var(--hero-ground-h) * 0.42 + var(--rb-s) * 1.4);
  pointer-events: none;

  // Moonlit night (default theme).
  --rb-fur: #5f5a55;
  --rb-furhi: #86827d;
  --rb-hi-o: 0.22;
  --rb-shade: #4c4843;
  --rb-deep: #3d3a36;
  --rb-belly: #8d8882;
  --rb-earin: #80736f;
  --rb-eye: #0d0c0b;
  --rb-iris: #0d0c0b;
  --rb-nose: #45403c;
  --rb-tip: #262320;
  --rb-line: #2f2b28;
  --rb-catch: #ffffff;
  --rb-whisker: #e4ecff;
  --rb-whisker-o: 0.4;
  --rb-tail: #a3a7ac;
  --rb-tail-edge: #71767c;
  --rb-under-o: 0.55;
  --rb-soft-o: 0.5;
  --rb-rim: #c8dcff;
  --rb-rim-o: 0.45;
  --rb-shadow: #000000;
  --rb-shadow-o: 0.42;
  --rb-shadow-o2: 0.2;
  --rb-earth: #1d1915;
  --rb-earthhi: #2a241e;
  --rb-crumb: #6a5a48;
  --rb-puff: #a9b8b0;
  --rb-puff-o: 0.3;
  --rb-puff-o2: 0.14;
  --rb-hole: #030202;
  --rb-holein: #1a140f;
  --rb-mound: #12301a;
  --rb-moundhi: #1a3d22;
  --rb-blade: #1f4a28;
  --rb-bladehi: #2c6034;
  --rb-flower: #a9ae98;
  --rb-pollen: #8f8a5c;
  --rb-flower-o: 0.45;
}

// Soft morning.
[data-theme="light"] .hero-rabbit {
  --rb-fur: #8b7a69;
  --rb-furhi: #a99784;
  --rb-hi-o: 0.4;
  --rb-shade: #76665a;
  --rb-deep: #62554a;
  --rb-belly: #c9bcab;
  --rb-earin: #a88f86;
  --rb-eye: #17120f;
  --rb-iris: #17120f;
  --rb-nose: #6a5a4f;
  --rb-tip: #3a3029;
  --rb-line: #4a3d34;
  --rb-catch: #ffffff;
  --rb-whisker: #fffaf0;
  --rb-whisker-o: 0.6;
  --rb-tail: #e9e2d7;
  --rb-tail-edge: #d6cec3;
  --rb-under-o: 0.42;
  --rb-soft-o: 0.42;
  --rb-rim: #fff1d6;
  --rb-rim-o: 0;
  --rb-shadow: #2e5a22;
  --rb-shadow-o: 0.34;
  --rb-shadow-o2: 0.15;
  --rb-earth: #7f6549;
  --rb-earthhi: #957a5a;
  --rb-crumb: #8b6a47;
  --rb-puff: #f6ecd2;
  --rb-puff-o: 0.75;
  --rb-puff-o2: 0.36;
  --rb-hole: #20140b;
  --rb-holein: #4d3521;
  --rb-mound: #78b05a;
  --rb-moundhi: #8cc46a;
  --rb-blade: #6aa64f;
  --rb-bladehi: #9fd27a;
  --rb-flower: #fff8e1;
  --rb-pollen: #ffd36e;
  --rb-flower-o: 1;
}

.hero-rabbit__stage {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(var(--hero-ground-h) * 0.42 - var(--rb-s) * 0.3);
  height: calc(var(--rb-s) * 1.7);
  transform: translate3d(calc(var(--hero-px, 0) * 14px), 0, 0);
}

.hero-rabbit__svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

// Theme colours: every fill/stroke/stop reads a custom property and eases
// to the new value when data-theme flips (dawn <-> dusk).
$rb-theme-t: 1.2s ease;
$rb-fills: fur, furhi, shade, deep, belly, earin, eye, nose, tip, catch, earth, earthhi, crumb, blade, bladehi, flower, pollen;

@each $name in $rb-fills {
  .f-#{$name} {
    fill: var(--rb-#{$name});
    transition: fill $rb-theme-t;
  }
}

.s-line {
  stroke: var(--rb-line);
  transition: stroke $rb-theme-t;
}

.s-shade {
  stroke: var(--rb-shade);
  transition: stroke $rb-theme-t;
}

.s-rim {
  stroke: var(--rb-rim);
  stroke-opacity: 0.5;
  transition: stroke $rb-theme-t;
}

.s-whisker {
  stroke: var(--rb-whisker);
  opacity: var(--rb-whisker-o);
  transition: stroke $rb-theme-t, opacity $rb-theme-t;
}

.rb-rim {
  opacity: var(--rb-rim-o);
  transition: opacity $rb-theme-t;
}

.rb-flowers {
  opacity: var(--rb-flower-o);
  transition: opacity $rb-theme-t;
}

@mixin rb-stop($color, $opacity) {
  stop-color: var($color);
  stop-opacity: $opacity;
  transition: stop-color $rb-theme-t, stop-opacity $rb-theme-t;
}

.rb-g-shadow-0 { @include rb-stop(--rb-shadow, var(--rb-shadow-o)); }
.rb-g-shadow-1 { @include rb-stop(--rb-shadow, var(--rb-shadow-o2)); }
.rb-g-shadow-2 { @include rb-stop(--rb-shadow, 0); }
.rb-g-puff-0 { @include rb-stop(--rb-puff, var(--rb-puff-o)); }
.rb-g-puff-1 { @include rb-stop(--rb-puff, var(--rb-puff-o2)); }
.rb-g-puff-2 { @include rb-stop(--rb-puff, 0); }
.rb-g-hi-0 { @include rb-stop(--rb-furhi, var(--rb-hi-o)); }
.rb-g-hi-1 { @include rb-stop(--rb-furhi, 0); }
.rb-g-soft-0 { @include rb-stop(--rb-shade, var(--rb-soft-o)); }
.rb-g-soft-1 { @include rb-stop(--rb-shade, 0); }
.rb-g-cheek-0 { @include rb-stop(--rb-belly, 0.42); }
.rb-g-cheek-1 { @include rb-stop(--rb-belly, 0); }
.rb-g-under-0 { @include rb-stop(--rb-shade, 0); }
.rb-g-under-1 { @include rb-stop(--rb-shade, var(--rb-under-o)); }
.rb-g-rim-0 { @include rb-stop(--rb-rim, 0.6); }
.rb-g-rim-1 { @include rb-stop(--rb-rim, 0); }
.rb-g-iris-0 { @include rb-stop(--rb-iris, 1); }
.rb-g-iris-1 { @include rb-stop(--rb-eye, 1); }
.rb-g-tail-0 { @include rb-stop(--rb-tail, 1); }
.rb-g-tail-1 { @include rb-stop(--rb-tail-edge, 1); }
.rb-g-tail-2 { @include rb-stop(--rb-tail-edge, 0.55); }
.rb-g-hole-0 { @include rb-stop(--rb-hole, 1); }
.rb-g-hole-1 { @include rb-stop(--rb-holein, 1); }
.rb-g-mound-0 { @include rb-stop(--rb-moundhi, 1); }
.rb-g-mound-1 { @include rb-stop(--rb-mound, 1); }
.rb-g-mound-2 { @include rb-stop(--rb-mound, 0); }

// The burrow (and the copy of its front lip) settles in with the meadow
// during the entrance.
.rb-burrow-in {
  animation: rb-burrow-in 0.9s 1.05s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes rb-burrow-in {
  from {
    opacity: 0;
    transform: translate(0, 6px);
  }
  to {
    opacity: 1;
    transform: translate(0, 0);
  }
}

// The still frame (heroState.reducedMotion, which also pauses every scene
// animation) shows the burrow in place, never frozen at its first keyframe.
.is-still .rb-burrow-in {
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  .rb-burrow-in {
    animation: none;
  }
}
</style>
